import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "../renewal/Renew.module.css";
import Navbar from "../../components/ui/Navbar";
import Footer from "../../components/ui/Footer";
// import { useNavigate } from "react-router";
import Menu from "../../components/ui/Menu";
import DistributorApi from "../../utils/api/DistributorApi";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import modalStyle from "../customers/Customers.module.css";
import copy from "copy-to-clipboard";
import Papa from "papaparse";
import { userDataActions } from "../../store/redux-store/Index";
import pinStyles from "./FixedAgentPins.module.css";
import { useDebouncedSearch } from "../../hooks/useDebouncedValue";

const PIN_PLATFORMS = ["NICON", "AG", "AGP", "FGI", "FGIP", "IEI"];

const PIN_TYPE_SUMMARY = [
  { value: "Commercial", label: "Commercial" },
  { value: "PrivateMotor", label: "Private Motor" },
  { value: "TriCycle", label: "Tri Cycle" },
  { value: "MotorCycle", label: "Motor Cycle" },
  { value: "Truck", label: "Truck" },
];

const SHEET_PRICED_PIN_TYPES = ["AG", "NICON"];

const SHEET_PRICE_COLUMN_SUFFIX_BY_TYPE = {
  PrivateMotor: "PMO",
  Commercial: "CVO",
};

const PRICE_SOURCE_LABELS = {
  sheet: "Sheet price applied",
  api: "API price applied",
  manual: "Manual price entered",
};

const TypeIcon = ({ type }) => {
  if (type === "Commercial" || type === "Truck") {
    return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 18H3a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2h4l3 3v7a2 2 0 0 1-2 2h-2"/><path d="M9 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/><path d="M19 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/></svg>;
  }

  if (type === "MotorCycle" || type === "TriCycle") {
    return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="18.5" cy="17.5" r="3.5"/><path d="M15 6a3.5 3.5 0 1 0-7 0"/><path d="M12 17.5V14l-3-3 4-3 2 3h2"/></svg>;
  }

  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a2 2 0 0 0-1.6-.8H7.3a2 2 0 0 0-1.6.8L3 11l-5.16.86a1 1 0 0 0-.84.99V16h3"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="17.5" cy="16.5" r="2.5"/></svg>;
};

const PIN_PRICE_SHEET_FALLBACK_URL =
  "https://docs.google.com/spreadsheets/d/e/2PACX-1vRncoxRMe5DLTXj_8JWh-5KROgrWnAq3MSr5E-AMFdcgWYOHeKauKVALEX1hA_Tj_E3ogak8ArZ5PRU/pub?gid=1655259831&single=true&output=csv";

const getPinPriceSheetUrl = () =>
  process.env.REACT_APP_PIN_PRICE_SHEET_URL?.trim() ||
  PIN_PRICE_SHEET_FALLBACK_URL;

const normalizePricingText = (value) =>
  value?.toString().toLowerCase().replace(/\s+/g, " ").trim() || "";

const getSpreadsheetValue = (row, fieldName) => {
  if (!row) return "";

  return (
    row[fieldName] ||
    row[Object.keys(row).find((key) => key.trim() === fieldName)] ||
    ""
  );
};

const parsePriceNumber = (value) => {
  const numericValue = value?.toString().replace(/,/g, "").trim();
  if (!numericValue) return null;

  const parsedValue = Number(numericValue);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const getSheetPriceForSelection = ({ rows, agentName, pinType, vehicleType }) => {
  const normalizedPinType = pinType?.toString().toUpperCase();
  const columnSuffix = SHEET_PRICE_COLUMN_SUFFIX_BY_TYPE[vehicleType];

  if (
    !Array.isArray(rows) ||
    !rows.length ||
    !agentName ||
    !columnSuffix ||
    !SHEET_PRICED_PIN_TYPES.includes(normalizedPinType)
  ) {
    return null;
  }

  const matchedAgentRow = rows.find(
    (row) =>
      normalizePricingText(getSpreadsheetValue(row, "Name")) ===
      normalizePricingText(agentName)
  );

  if (!matchedAgentRow) return null;

  return parsePriceNumber(
    getSpreadsheetValue(matchedAgentRow, `${normalizedPinType} ${columnSuffix}`)
  );
};

const normalizeRows = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

function DistributorPinList(props) {
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [productMeta, setProductMeta] = useState(null);
  const [platformSummary, setPlatformSummary] = useState({});
  const [exportOption, setExportOption] = useState("");
  const [productData, setProductData] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [fixedAgent, setFixedAgent] = useState("");
  const [fundAgentModal, setFundAgentModal] = useState(false);
  const [amount, setAmount] = useState(0);
  const [count, setCount] = useState();
  const [pinPlatform, setPinPlatform] = useState("");
  const [productType, setProductType] = useState("");
  const [expandedPlatforms, setExpandedPlatforms] = useState({
    [PIN_PLATFORMS[0]]: true,
  });
  const [exportContent, setExportContent] = useState(null);
  const [type, setType] = useState("");
  const [price, setPrice] = useState(0);
  const [priceSource, setPriceSource] = useState("");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const {
    inputValue: agentSearchTerm,
    setInputValue: setAgentSearchTerm,
    query: debouncedAgentSearch,
    isPending: isAgentSearchPending,
  } = useDebouncedSearch("", { delay: 700, minLength: 2 });
  const [agentPage, setAgentPage] = useState(1);
  const [agentPagination, setAgentPagination] = useState({
    page: 1,
    lastPage: 1,
    total: 0,
  });
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false);
  const [selectedAgentLabel, setSelectedAgentLabel] = useState("");
  const agentDropdownRef = useRef(null);

  const token = useSelector((state) => state.userData.loginData.token);
  const pinPriceSheet = useSelector((state) => state.userData.pinPriceSheet) || {
    data: [],
  };

  //   const navigate = useNavigate();

  const [distributors, setDistributors] = useState([]);
  const activePinCount = Number(productMeta?.total ?? 0);

  const togglePlatform = (platform) => {
    setExpandedPlatforms((current) => ({
      ...current,
      [platform]: !current[platform],
    }));
  };

  const openSellModal = (platform) => {
    setProductType(platform);
    setProductData({ platform });
    setModalOpen(true);
    fecthPinsCallback(platform);
  };

  const openExportModal = (platform) => {
    exportPinModalOpen(platform);
  };
  const searchedAgents = distributors.filter((d) => {
    // Until a query has settled, filter the already loaded rows locally. This
    // keeps backspacing responsive without firing an API request per keypress.
    if (!agentSearchTerm.trim()) return true;
    const term = agentSearchTerm.toLowerCase();
    return (
      d.fullname?.toLowerCase().includes(term) ||
      d.email?.toLowerCase().includes(term) ||
      d.phone_no?.toString().includes(term) ||
      d.wallet?.business_name?.toLowerCase().includes(term)
    );
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        agentDropdownRef.current &&
        !agentDropdownRef.current.contains(e.target)
      ) {
        setAgentDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const selectedAgentName =
    selectedAgentLabel ||
    distributors.find((d) => d.email === fixedAgent)?.fullname ||
    "";

  const priceSourceNote = PRICE_SOURCE_LABELS[priceSource] || "";

  const fetchDistributors = useCallback(async () => {
    if (isAgentSearchPending) return;

    try {
      const response = await DistributorApi.getAllAgents({
        token,
        page: agentPage,
        perPage: 25,
        search: debouncedAgentSearch.trim(),
      });
      const agentPageData = response.data?.data;
      const nextAgents = normalizeRows(agentPageData);

      setDistributors((current) => {
        if (agentPage === 1) return nextAgents;

        const knownEmails = new Set(current.map((agent) => agent.email));
        return [...current, ...nextAgents.filter((agent) => !knownEmails.has(agent.email))];
      });
      setAgentPagination({
        page: Number(agentPageData?.current_page ?? agentPage),
        lastPage: Number(agentPageData?.last_page ?? 1),
        total: Number(agentPageData?.total ?? nextAgents.length),
      });
    } catch (err) {
      toast.error(err?.response?.data?.message, {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
    } finally {
      setIsLoadingAgents(false);
    }
  }, [agentPage, debouncedAgentSearch, isAgentSearchPending, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const sellPinRes = await DistributorApi.newSellPin({
        token,
        count: amount,
        fixedagent_email: fixedAgent,
        export_option: exportOption,
        pin_type: productData.platform,
        type: type,
        unit_price: Number(price?.toString().replace(/,/g, "")),
      });
      setIsLoading(false);
      toast.success(sellPinRes?.data?.message, {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
      fecthPinsCallback();
      setConfirmModalOpen(false);
    } catch (err) {
      setIsLoading(false);
      toast.error(err?.response?.data?.message, {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
    }
  };

  useEffect(() => {
    setIsLoadingAgents(true);
    fetchDistributors();
  }, [fetchDistributors]);

  useEffect(() => {
    const fetchPinPriceSheet = async () => {
      const sheetUrl = getPinPriceSheetUrl();

      if (!sheetUrl) {
        dispatch(
          userDataActions.setPinPriceSheetError(
            "PIN price sheet URL is not configured"
          )
        );
        return;
      }

      try {
        dispatch(userDataActions.setPinPriceSheetLoading(true));

        const response = await DistributorApi.fetchPinPriceSpreadsheet({
          sheetUrl,
        });
        const responseText =
          typeof response.data === "string" ? response.data : "";
        const parsedData = Papa.parse(responseText, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (header) => header.trim(),
        });

        if (parsedData.errors.length > 0) {
          throw new Error("Failed to parse PIN price sheet");
        }

        dispatch(userDataActions.setPinPriceSheetData(parsedData.data));
      } catch (error) {
        dispatch(
          userDataActions.setPinPriceSheetError(
            error?.message || "Failed to load PIN price sheet"
          )
        );
      }
    };

    fetchPinPriceSheet();
  }, [dispatch]);

  useEffect(() => {
    let isCurrent = true;

    const getPriceList = async () => {
      if (!productType || !type) {
        setPrice(0);
        setPriceSource("");
        setIsLoading(false);
        return;
      }

      const sheetPrice = getSheetPriceForSelection({
        rows: pinPriceSheet.data,
        agentName: selectedAgentName,
        pinType: productType,
        vehicleType: type,
      });

      if (sheetPrice) {
        setPrice(sheetPrice.toLocaleString());
        setPriceSource("sheet");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const priceRes = await DistributorApi.getPrice({
          token,
          pinType: productType,
          type,
        });
        if (!isCurrent) return;

        setPrice(priceRes?.data?.data?.price?.toLocaleString() || 0);
        setPriceSource("api");
        setIsLoading(false);
      } catch (err) {
        if (!isCurrent) return;

        setIsLoading(false);
        setPriceSource("");
        toast.error(err?.response?.data?.message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
        });
      }
    };
    getPriceList();

    return () => {
      isCurrent = false;
    };
  }, [token, productType, type, selectedAgentName, pinPriceSheet.data]);

  const fecthPinsCallback = useCallback((platform = productType) => {
    if (!platform) return;

    const fetchCartHistory = async () => {
      try {
        setIsLoading(true);
        const response = await DistributorApi.getNewPinList({
          token,
          status: "ACTIVE",
          platform,
          type: "",
          page: 1,
        });
        const data = response.data?.data;
        setProducts(normalizeRows(data));
        setProductMeta(data);
        setPlatformSummary(response.data?.platform_summary || {});
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        toast.error(err?.response?.data?.message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
        });
      }
    };
    fetchCartHistory();
  }, [productType, token]);

  useEffect(() => {
    const fetchPlatformSummary = async () => {
      try {
        setIsLoading(true);
        const response = await DistributorApi.getNewPinList({
          token,
          status: "ACTIVE",
          type: "",
          page: 1,
        });
        setPlatformSummary(response.data?.platform_summary || {});
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load PIN inventory", {
          position: "top-left",
          theme: "dark",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlatformSummary();
  }, [token]);

  const exportPinModalOpen = (data) => {
    setPinPlatform(data);
    setFundAgentModal(true);
  };

  const exportPin = async () => {
    setIsLoading(true);

    try {
      const response = await DistributorApi.newExportPins({
        token,
        count: parseInt(count),
        pinType: pinPlatform,
        email: fixedAgent,
        type,
      });

      const pinCodes = response.data
        .split("\n")
        .slice(1)
        .map((row) => row.split(",")[1]);
      copy(pinCodes.join("\n"));
      toast.success("Copied to clipboard!", {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
      setExportContent(pinCodes.join("\n"));
      // const blob = new Blob([response.data], { type: "text/csv" });
      // const url = URL.createObjectURL(blob);
      // const a = document.createElement("a");
      // a.href = url;
      // a.download = `export_${pinPlatform}_${Date.now()}.csv`;
      // a.setAttribute("allow-downloads", "");
      // document.body.appendChild(a);
      // a.click();
      // document.body.removeChild(a);
      // URL.revokeObjectURL(url);
      toast.success("Pins exported successfully", {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
      setIsLoading(false);

      setCount("");
    } catch (error) {
      setIsLoading(false);

      toast.error(error.response.data.message, {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
      console.error("Failed to export pins", error);
    }
  };

  const share = () => {
    const isMobile = () =>
      /Mobi|Android|iPhone|iPad|iPod|Windows Phone/i.test(navigator.userAgent);

    if (isMobile() && navigator.share) {
      console.log(pinPlatform);
      navigator
        .share({
          text: exportContent,
        })
        .then(() => {
          setFundAgentModal(false);
          setExportContent(null);

          console.log("Successful share");
        })
        .catch((error) => console.log("Error sharing", error));
    } else {
      console.log("Web Share API not supported.");
    }
  };
  const formatNumber = (value) => {
    const numericValue = value.replace(/,/g, "");
    if (numericValue === "") return "";

    const number = Number(numericValue);
    if (isNaN(number)) return value;

    return number.toLocaleString("en-US");
  };

  const handleChange = (e) => {
    const input = e.target.value.replace(/[^0-9.]/g, "");
    const formattedInput = formatNumber(input);
    setPrice(formattedInput);
    setPriceSource("manual");
  };

  /* ── Searchable Agent Dropdown (reused in both modals) ── */
  const agentDropdownStyles = {
    wrapper: {
      position: "relative",
      width: "100%",
    },
    trigger: {
      width: "100%",
      height: 44,
      padding: "8px 12px",
      border: "1px solid #8EA396",
      borderRadius: 6,
      background: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      cursor: "pointer",
      fontFamily: "Circular Std, sans-serif",
      fontSize: 14,
      color: fixedAgent ? "#14131a" : "#999",
      WebkitAppearance: "none",
      boxSizing: "border-box",
    },
    chevron: {
      fontSize: 10,
      color: "#888",
      marginLeft: 8,
      transition: "transform 0.2s ease",
    },
    panel: {
      position: "absolute",
      top: "calc(100% + 4px)",
      left: 0,
      right: 0,
      background: "#fff",
      border: "1px solid #dde3ea",
      borderRadius: 8,
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      zIndex: 2000,
      maxHeight: 260,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    searchBox: {
      width: "100%",
      padding: "10px 12px",
      border: "none",
      borderBottom: "1px solid #eee",
      outline: "none",
      fontSize: 14,
      fontFamily: "Circular Std, sans-serif",
      boxSizing: "border-box",
      WebkitAppearance: "none",
      borderRadius: 0,
    },
    list: {
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      flex: 1,
    },
    item: (isSelected) => ({
      padding: "10px 12px",
      cursor: "pointer",
      fontSize: 14,
      fontFamily: "Circular Std, sans-serif",
      color: "#14131a",
      background: isSelected ? "#EEF0FF" : "transparent",
      borderBottom: "1px solid #f5f5f5",
      transition: "background 0.15s ease",
      textAlign: "left",
    }),
    itemSub: {
      fontSize: 11,
      color: "#888",
      marginTop: 2,
    },
    empty: {
      padding: "14px 12px",
      fontSize: 13,
      color: "#aaa",
      textAlign: "center",
    },
  };

  const renderAgentDropdown = () => (
    <div
      ref={agentDropdownRef}
      style={agentDropdownStyles.wrapper}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Trigger button */}
      <div
        style={agentDropdownStyles.trigger}
        onClick={() => {
          setAgentDropdownOpen((prev) => !prev);
          setAgentSearchTerm("");
          setAgentPage(1);
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selectedAgentName || "Select Fixed Agent"}
        </span>
        <span
          style={{
            ...agentDropdownStyles.chevron,
            transform: agentDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          ▼
        </span>
      </div>

      {/* Dropdown panel */}
      {agentDropdownOpen && (
        <div style={agentDropdownStyles.panel}>
          <input
            type="text"
            placeholder="Search by name, email, or phone…"
            value={agentSearchTerm}
            onChange={(e) => {
              setAgentSearchTerm(e.target.value);
              setAgentPage(1);
            }}
            style={agentDropdownStyles.searchBox}
            autoFocus
          />
          <div style={agentDropdownStyles.list}>
            {isAgentSearchPending && searchedAgents.length === 0 ? (
              <div style={agentDropdownStyles.empty}>Waiting for you to finish typing…</div>
            ) : searchedAgents.length === 0 ? (
              <div style={agentDropdownStyles.empty}>No agents found</div>
            ) : (
              searchedAgents.map((agent) => (
                <div
                  key={agent.id}
                  style={agentDropdownStyles.item(agent.email === fixedAgent)}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#f0f2ff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background =
                      agent.email === fixedAgent ? "#EEF0FF" : "transparent")
                  }
                  onClick={() => {
                    setFixedAgent(agent.email);
                    setSelectedAgentLabel(agent.fullname || "");
                    setAgentDropdownOpen(false);
                    setAgentSearchTerm("");
                  }}
                >
                  {agent.fullname}
                </div>
              ))
            )}
            {agentPage < agentPagination.lastPage && (
              <button
                type="button"
                onClick={() => setAgentPage((page) => page + 1)}
                disabled={isLoadingAgents}
                style={{
                  border: "none",
                  borderTop: "1px solid #eee",
                  background: "#f8fafc",
                  color: "#1814f3",
                  cursor: isLoadingAgents ? "not-allowed" : "pointer",
                  fontFamily: "Circular Std, sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  padding: "11px 12px",
                  width: "100%",
                }}
              >
                {isLoadingAgents
                  ? "Loading agents…"
                  : `Load more agents (${distributors.length} of ${agentPagination.total})`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <ToastContainer />
      {fundAgentModal && (
        <div
          className={modalStyle.modal_overlay}
          onClick={() => setFundAgentModal(false)}
        >
          <div
            className={modalStyle.modal_card}
            onClick={(e) => e.stopPropagation()}
          >
            <h1 className={modalStyle.modal_title}>Export Pin</h1>
            <div>
              {/* <p className={modalStyle.modal_description}>Fixed Agent</p> */}
              {renderAgentDropdown()}

              <input
                className={modalStyle.modal_input}
                type="num"
                onChange={(e) => setCount(e.target.value)}
                value={count}
                placeholder="How many do you want to export"
              />

              {/* <p className={styles.modal_description}>Type</p> */}
              <select
                className={modalStyle.modal_input}
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="">Select Type</option>
                {PIN_TYPE_SUMMARY.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              {!exportContent ? (
                <button
                  disabled={isLoading}
                  className={modalStyle.modal_button}
                  onClick={() => exportPin()}
                >
                  {isLoading ? `Loading...` : `Export`}
                </button>
              ) : (
                <button
                  className={modalStyle.modal_button2}
                  onClick={() => share()}
                >
                  {isLoading ? `Loading...` : `Share Now`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {modalOpen && (
        <div
          className={styles.modal_overlay}
          onClick={() => setModalOpen(false)}
        >
          <div
            className={styles.modal_card}
            onClick={(e) => e.stopPropagation()}
          >
            <h1 className={styles.modal_title}>
              {/* {productsDetails?.product_name} */}
            </h1>
            {/* <p className={styles.modal_description}>Pin Type </p>
            <select
              className={styles.modal_input}
              value={pinType}
              onChange={(e) => setPinType(e.target.value)}
            >
              <option value="">Select Pin Type</option>
              <option value="IEI">IEI</option>
              <option value="CEI">CEI</option>
            </select> */}
            <p className={styles.modal_description}>Fixed Agent</p>
            {renderAgentDropdown()}

            <p className={styles.modal_description}>Amount </p>
            <input
              className={styles.modal_input}
              type="num"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
            />

            <p className={styles.modal_description}>Export Option</p>
            <select
              className={styles.modal_input}
              value={exportOption}
              onChange={(e) => setExportOption(e.target.value)}
            >
              <option value="">Select Export Option</option>
              <option value="YES">YES</option>
              <option value="NO">NO</option>
            </select>

            <p className={styles.modal_description}>Type</p>
            <select
              className={styles.modal_input}
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="">Select Export Option</option>
              {PIN_TYPE_SUMMARY.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <p className={styles.modal_description}>Price </p>
            <input
              className={styles.modal_input}
              type="text"
              value={price}
              onChange={handleChange}
            />
            {priceSourceNote && (
              <p
                className={styles.modal_description}
                style={{ fontSize: 12, marginTop: 4 }}
              >
                {priceSourceNote}
              </p>
            )}

            <button
              className={styles.modal_button}
              onClick={() => {
                setConfirmModalOpen(true);
                setModalOpen(false);
              }}
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "Proceed"}
            </button>
          </div>
        </div>
      )}

      {confirmModalOpen && (
        <div
          className={styles.modal_overlay}
          onClick={() => setConfirmModalOpen(false)}
        >
          <div
            className={styles.modal_card}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className={styles.modal_title}>Confirm Details</h2>
            <p className={styles.modal_description}>
              Name: {selectedAgentName}
            </p>

            <p className={styles.modal_description}>Amount: {amount}</p>
            <p className={styles.modal_description}>
              Export Option: {exportOption}
            </p>
            <p className={styles.modal_description}>Type: {type}</p>
            <p className={styles.modal_description}>Price: {price}</p>
            <button
              className={styles.modal_button}
              onClick={(e) => {
                handleSubmit(e);
              }}
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "Submit"}
            </button>
          </div>
        </div>
      )}

      <div className="menu" onClick={() => props.setShowMenu(false)}>
        {props.showMenu && <Menu setShowMenu={props.setShowMenu} />}
      </div>

      <Navbar title="Pin" setShowMenu={props.setShowMenu} />
      <div className={pinStyles.pageBody}>
        {isLoading && products.length === 0 ? (
          <div className={pinStyles.stack}>
            {[1, 2, 3].map((number) => (
              <div key={number} className={`${pinStyles.card} ${pinStyles.skeletonCard}`}>
                <div className={pinStyles.skeletonHeader}>
                  <div className={`${pinStyles.skeletonBox} ${pinStyles.skeletonLogoBox}`} />
                  <div className={pinStyles.skeletonHeaderText}>
                    <div className={`${pinStyles.skeletonBox} ${pinStyles.skeletonLine} ${pinStyles.skeletonLineLg}`} />
                    <div className={`${pinStyles.skeletonBox} ${pinStyles.skeletonLine} ${pinStyles.skeletonLineSm}`} />
                  </div>
                  <div className={`${pinStyles.skeletonBox} ${pinStyles.skeletonBtn}`} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className={pinStyles.sectionTitle}>PIN INVENTORY BY INSURER</div>
            <div className={pinStyles.stack}>
              {PIN_PLATFORMS.map((platform) => {
                const isExpanded = expandedPlatforms[platform];
                const isSelectedPlatform = productType === platform;
                const inventory = Number(platformSummary?.[platform] ?? 0);
                const activeCount = isSelectedPlatform ? activePinCount : null;

                return (
                  <div className={pinStyles.card} key={platform}>
                    <div className={pinStyles.cardHeader} onClick={() => togglePlatform(platform)}>
                      <div className={`${pinStyles.logoBox} ${pinStyles[platform.toLowerCase()] || ""}`}>
                        {platform}
                      </div>
                      <div className={pinStyles.headerContent}>
                        <h3 className={pinStyles.insurerName}>{platform}</h3>
                        <p className={pinStyles.insurerSubtitle}>Distributor inventory</p>
                      </div>
                      <div className={pinStyles.headerActions}>
                        <button
                          type="button"
                          className={pinStyles.exportBtn}
                          onClick={(event) => {
                            event.stopPropagation();
                            openSellModal(platform);
                          }}
                        >
                          Sell Pins
                        </button>
                        <div className={`${pinStyles.chevron} ${isExpanded ? pinStyles.expanded : ""}`}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className={pinStyles.cardBody}>
                        <div className={pinStyles.typeItem}>
                          <div className={pinStyles.typeHeader}>
                            <span className={pinStyles.typeIcon}><TypeIcon type="PrivateMotor" /></span>
                            <span className={pinStyles.typeName}>Active PINs</span>
                          </div>
                          <div className={pinStyles.typeCount}>
                            {activeCount === null ? "—" : activeCount.toLocaleString()}
                          </div>
                          <div className={pinStyles.footerNote}>
                            {activeCount === null ? "Load when selling" : "Available to sell"}
                          </div>
                        </div>
                        <div className={pinStyles.typeItem}>
                          <div className={pinStyles.typeHeader}>
                            <span className={pinStyles.typeIcon}><TypeIcon type="Commercial" /></span>
                            <span className={pinStyles.typeName}>Total Inventory</span>
                          </div>
                          <div className={pinStyles.typeCount}>{inventory.toLocaleString()}</div>
                          <div className={pinStyles.footerNote}>Across all PIN types</div>
                        </div>
                        <div className={pinStyles.typeItem}>
                          <div className={pinStyles.typeHeader}>
                            <span className={pinStyles.typeIcon}><TypeIcon type="MotorCycle" /></span>
                            <span className={pinStyles.typeName}>Distribution</span>
                          </div>
                          <button
                            type="button"
                            className={pinStyles.exportBtn}
                            onClick={() => openExportModal(platform)}
                          >
                            Export Pins
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default DistributorPinList;
